import { HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ENDPOINTS } from 'app/core/constants/endpoints';
import { SKIP_AUTH_REFRESH } from 'app/core/interceptors/http-context-tokens';
import { ApiService } from 'app/core/services/api.service';
import { RealtimeService } from 'app/core/services/realtime.service';
import { BaseResponse } from 'app/shared/models/base-response';
import { ErrorHandlerService } from 'app/shared/services/error-handler.service';
import { DataStorageService } from 'app/shared/utils/data-storage-service';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AgentLoginRequest } from '../models/agent-login-request.model';
import { AgentLoginResponse } from '../models/agent-login-response.model';
import { AgentRegisterResponse } from '../models/agent-register-response.model';
import { User } from '../models/user.model';

/** Non-secret marker: the JWT is an httpOnly cookie the page can't read. */
const SIGNED_IN_KEY = 'signed_in';
/** Where pre-cookie builds kept the raw token. */
const LEGACY_TOKEN_KEY = 'token';

@Injectable({ providedIn: 'root' })
export class AuthService extends ErrorHandlerService {
  constructor(
    private apiService: ApiService,
    private dataStorageService: DataStorageService,
    private realtimeService: RealtimeService
  ) {
    super();
  }

  /**
   * * Register New Agent
   * @param payload AgentRegisterRequest
   * @returns Observable<AgentRegisterResponse>
   */
  registerAgent(payload: FormData): Observable<AgentRegisterResponse> {
    return this.apiService.post<AgentRegisterResponse>(
      ENDPOINTS.AGENT.REGISTER,
      payload,
      { context: new HttpContext().set(SKIP_AUTH_REFRESH, true) }
    );
  }

  /**
   * * Login Agent
   * @param payload AgentLoginRequest
   * @returns Observable<AgentLoginResponse>
   */
  loginAgent(payload: AgentLoginRequest): Observable<AgentLoginResponse> {
    return this.apiService.post<AgentLoginResponse>(
      ENDPOINTS.AGENT.LOGIN,
      payload,
      { context: new HttpContext().set(SKIP_AUTH_REFRESH, true) }
    );
  }

  /**
   * * Verify Email
   * @param token string
   * @returns Observable<BaseResponse>
   */
  verifyEmail(token: string): Observable<BaseResponse> {
    return this.apiService.get<BaseResponse>(
      ENDPOINTS.AUTH.VERIFY_EMAIL,
      { token: token },
      new HttpContext().set(SKIP_AUTH_REFRESH, true)
    );
  }

  /**
   * * Resend Verification Email
   * @returns Observable<BaseResponse>
   */
  resendVerificationEmail(email: string): Observable<BaseResponse> {
    return this.apiService.post<BaseResponse>(
      ENDPOINTS.AUTH.RESEND_VERIFICATION,
      { email: email },
      { context: new HttpContext().set(SKIP_AUTH_REFRESH, true) }
    );
  }

  /**
   * * Set Password for First Time
   * @param token string
   * @param password string
   * @param password_confirmation string
   * @returns Observable<BaseResponse>
   */
  setPassword(token: string, password: string, password_confirmation: string): Observable<BaseResponse> {
    return this.apiService.post<BaseResponse>(
      ENDPOINTS.AUTH.SET_PASSWORD,
      {
        token: token,
        password: password,
        password_confirmation: password_confirmation
      },
      { context: new HttpContext().set(SKIP_AUTH_REFRESH, true) }
    );
  }

  /**
   * * Logout Agent
   * * This method will clear the session and local storage.
   * * It will also invalidate the token on the server.
   * @returns Observable<BaseResponse>
   */
  logout(): Observable<BaseResponse> {
    return this.apiService.post<BaseResponse>(ENDPOINTS.AUTH.LOGOUT, null);
  }

  refreshToken(): Observable<any> {
    return this.apiService
      .post<any>(ENDPOINTS.AGENT.REFRESH, {}, {
        context: new HttpContext().set(SKIP_AUTH_REFRESH, true),
      })
      .pipe(
      tap((response) => {
        if (response.status?.success) {
          // The new token arrived as an httpOnly cookie; a legacy one is now superseded.
          this.removeLegacyToken();
          if (!this.isLoggedIn()) {
            this.dataStorageService.set(SIGNED_IN_KEY, '1');
          }
        }
      })
    );
  }

  /**
   * Pre-cookie builds stored the raw token in local/sessionStorage. Swap it
   * for a cookie once (refresh sends it as a bearer header via
   * AuthInterceptor), so signed-in agents aren't logged out by this release.
   */
  migrateLegacyToken(): Promise<void> {
    if (!this.getLegacyToken()) {
      return Promise.resolve();
    }

    const remembered = !!this.dataStorageService.get(LEGACY_TOKEN_KEY);
    return new Promise((resolve) => {
      this.refreshToken().subscribe({
        next: () => {
          this.markSignedIn(remembered);
          resolve();
        },
        error: (error) => {
          if (error?.status === 401 || error?.status === 403) {
            this.clearSession();
          }
          resolve();
        },
      });
    });
  }

  clearSession(): void {
    this.realtimeService.disconnect();
    sessionStorage.clear();
    this.dataStorageService.clearAll();
  }

  /**
   * The JWT itself is an httpOnly cookie the page can't read; this only
   * keeps the non-secret "signed in" marker and display data. Not
   * remembered = sessionStorage + a browser-session cookie (X-Remember-Me).
   */
  storeUserData(userData: User, email: string, rememberMe: boolean): void {
    this.removeLegacyToken();
    this.markSignedIn(rememberMe);
    if (rememberMe) {
      this.dataStorageService.set('user', JSON.stringify(userData));
      this.dataStorageService.set('email', email);
      return;
    } else {
      sessionStorage.setItem('user', JSON.stringify(userData));
      sessionStorage.setItem('email', email);
    }
  }

  private markSignedIn(rememberMe: boolean): void {
    if (rememberMe) {
      this.dataStorageService.set(SIGNED_IN_KEY, '1');
    } else {
      sessionStorage.setItem(SIGNED_IN_KEY, '1');
    }
  }

  getUser(): User {
    const user = sessionStorage.getItem('user');
    if (user) {
      return JSON.parse(user);
    } else {
      const user = this.dataStorageService.get('user');
      return user ? JSON.parse(user) : ({} as User);
    }
  }

  getBranches() {
    const userStr =
      sessionStorage.getItem('user') || this.dataStorageService.get('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      return user.agent && user.agent.branches ? user.agent.branches : [];
    }
    return [];
  }

  /** A raw token left by a pre-cookie build, until migrateLegacyToken() swaps it. */
  getLegacyToken(): string | null {
    return this.dataStorageService.get(LEGACY_TOKEN_KEY) || sessionStorage.getItem(LEGACY_TOKEN_KEY);
  }

  private removeLegacyToken(): void {
    this.dataStorageService.remove(LEGACY_TOKEN_KEY);
    sessionStorage.removeItem(LEGACY_TOKEN_KEY);
  }

  /** Whether the auth cookie should outlive the browser session. */
  isRemembered(): boolean {
    return this.dataStorageService.get(SIGNED_IN_KEY) === '1';
  }

  isLoggedIn(): boolean {
    return (
      this.dataStorageService.get(SIGNED_IN_KEY) === '1' ||
      sessionStorage.getItem(SIGNED_IN_KEY) === '1' ||
      !!this.getLegacyToken()
    );
  }
}
